import { DEFAULT_LOCALE, DEFAULT_TIMEZONE, FISCAL_YEAR_START_MONTH } from '../constants.js';

const EM_DASH = '—';

export type DateInput = Date | string | number | null | undefined;

export const toDate = (value: DateInput): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const dtf = (options: Intl.DateTimeFormatOptions) =>
  new Intl.DateTimeFormat(DEFAULT_LOCALE, { timeZone: DEFAULT_TIMEZONE, ...options });

/** `17 Aug 2026` — the default for table cells and detail rows. */
export const formatDate = (value: DateInput): string => {
  const date = toDate(value);
  if (!date) return EM_DASH;
  return dtf({ day: '2-digit', month: 'short', year: 'numeric' }).format(date);
};

/** `17 Aug 2026, 4:32 pm` */
export const formatDateTime = (value: DateInput): string => {
  const date = toDate(value);
  if (!date) return EM_DASH;
  return dtf({
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

export const formatTime = (value: DateInput): string => {
  const date = toDate(value);
  if (!date) return EM_DASH;
  return dtf({ hour: 'numeric', minute: '2-digit', hour12: true }).format(date);
};

/** `yyyy-MM-dd`, for date inputs and API query params. */
export const toDateInputValue = (value: DateInput): string => {
  const date = toDate(value);
  if (!date) return '';
  return date.toISOString().slice(0, 10);
};

export const daysBetween = (from: DateInput, to: DateInput = new Date()): number | null => {
  const a = toDate(from);
  const b = toDate(to);
  if (!a || !b) return null;
  const msPerDay = 86_400_000;
  return Math.round((b.getTime() - a.getTime()) / msPerDay);
};

/** Positive when overdue. Used by ageing buckets and follow-up escalation. */
export const daysOverdue = (dueDate: DateInput): number | null => daysBetween(dueDate);

export const isOverdue = (dueDate: DateInput, graceDays = 0): boolean => {
  const overdue = daysOverdue(dueDate);
  return overdue !== null && overdue > graceDays;
};

/** `2 days ago`, `in 3 days`, `just now`. */
export const formatRelative = (value: DateInput): string => {
  const date = toDate(value);
  if (!date) return EM_DASH;

  const diffMs = date.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(DEFAULT_LOCALE, { numeric: 'auto' });

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000_000],
    ['month', 2_592_000_000],
    ['week', 604_800_000],
    ['day', 86_400_000],
    ['hour', 3_600_000],
    ['minute', 60_000],
  ];

  for (const [unit, ms] of units) {
    if (abs >= ms) return rtf.format(Math.round(diffMs / ms), unit);
  }
  return 'just now';
};

/* ------------------------------------------------------------------ */
/* Fiscal year (April–March)                                           */
/* ------------------------------------------------------------------ */

export interface FiscalYear {
  startYear: number;
  endYear: number;
  /** `25-26`, used inside document numbers. */
  shortLabel: string;
  /** `FY 2025-26`, used in UI. */
  label: string;
  start: Date;
  end: Date;
}

export const getFiscalYear = (
  value: DateInput = new Date(),
  startMonth = FISCAL_YEAR_START_MONTH,
): FiscalYear => {
  const date = toDate(value) ?? new Date();
  const month = date.getMonth() + 1;
  const startYear = month >= startMonth ? date.getFullYear() : date.getFullYear() - 1;
  const endYear = startYear + 1;

  return {
    startYear,
    endYear,
    shortLabel: `${String(startYear).slice(2)}-${String(endYear).slice(2)}`,
    label: `FY ${startYear}-${String(endYear).slice(2)}`,
    start: new Date(Date.UTC(startYear, startMonth - 1, 1)),
    end: new Date(Date.UTC(endYear, startMonth - 1, 1)),
  };
};

/** Resolve a dashboard range key to an inclusive `[from, to]` window. */
export const resolveDateRange = (
  range: '7d' | '30d' | '90d' | 'mtd' | 'qtd' | 'ytd' | 'fy',
  now: Date = new Date(),
): { from: Date; to: Date } => {
  const to = now;
  const days = (n: number) => new Date(now.getTime() - n * 86_400_000);

  switch (range) {
    case '7d':
      return { from: days(7), to };
    case '30d':
      return { from: days(30), to };
    case '90d':
      return { from: days(90), to };
    case 'mtd':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to };
    case 'qtd': {
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      return { from: new Date(now.getFullYear(), quarterStartMonth, 1), to };
    }
    case 'ytd':
      return { from: new Date(now.getFullYear(), 0, 1), to };
    case 'fy':
      return { from: getFiscalYear(now).start, to };
  }
};
